import type { FunctionComponent } from 'react';
import { useEffect } from 'react';
import { logger } from '@rea-argonaut/observability';
import { mediaHelper, useToast } from '@property.com.au/ui-kit';
import { usePropertyId } from '../../../../../contexts/PropertyIdContext/PropertyIdContext';
import { useAccountQuery } from '../../../../../hooks/useAccountQuery';
import { useFollowedProperties } from '../../../../hooks/useFollowedProperties';
import { PropertyRelationship } from '../../../../../components/PropertyRelationship/PropertyRelationship';
import { trackSaveProperty } from '../../../../../analytics/event-tracking/trackSaveProperty';
import { useJoinUsUrl } from '../../../../../hooks/useJoinUsUrl';
import { clickThroughSourceTracking } from '../../../../../analytics/click-through-tracking/clickThroughSourceTracking';
import { PROPERTY_DETAIL_PAGE_TRACKING_SOURCE } from '../../../PropertyRelationship/tracking';
import { LogLabel } from '../../../../../logger/logLabel';
import { ButtonWithLoadingState } from '../ButtonWithLoadingState/ButtonWithLoadingState';
import type { CtaButtonProps } from '../../HeaderCtas';
import { isNotNullOrUndefinedValue } from '../../../../../helpers/isNotNullOrUndefinedValue';
import { useUrlContext } from '../../../../../contexts/UrlContext/UrlContext';
import { styled } from 'styled-components';
import { assignLocation } from '../../../../../helpers/assignLocation';

export const openSetRelationshipModalWithMyHomePreselectedUrlHash =
  '#open-set-relationship-modal-with-my-home-selected';

const StyledButtonWithLoadingState = styled(ButtonWithLoadingState)`
  width: 100%;
  ${mediaHelper('tabletMin')} {
    width: auto;
  }
`;

export const ThisIsMyHomeCta: FunctionComponent<{ cta: CtaButtonProps }> = ({ cta }) => {
  const accountQueryResult = useAccountQuery();
  const signedIn = isNotNullOrUndefinedValue(accountQueryResult.data?.account);
  const { followProperty, followedPropertiesError, loading } = useFollowedProperties();
  const propertyId = usePropertyId();
  const toast = useToast();
  const url = useUrlContext();
  const joinUsUrl = useJoinUsUrl(
    `${url.pathname}${openSetRelationshipModalWithMyHomePreselectedUrlHash}`,
  );

  const handleClick = async () => {
    if (signedIn) {
      await followProperty(propertyId, PropertyRelationship.myHome);
      trackSaveProperty({
        user_action: 'save',
        property_relationship: PropertyRelationship.myHome,
        in_page_source: ['property_page', 'header_cta', 'this_is_my_home_button'].join('__'),
      });
      return;
    }

    clickThroughSourceTracking({
      pathname: '/join-us/',
      page: PROPERTY_DETAIL_PAGE_TRACKING_SOURCE,
      element: [PROPERTY_DETAIL_PAGE_TRACKING_SOURCE, 'this_is_my_home_cta'].join('__'),
      sourceType: 'web',
    });

    assignLocation(joinUsUrl);
  };

  useEffect(() => {
    if (followedPropertiesError) {
      toast({
        message: 'Oops, something went wrong',
        placement: 'top',
        variant: 'warning',
      });
      logger.error({
        error: 'An error has occurred',
        description: 'Fetch - Failed to set my home relationship',
        customAttributes: {
          label: LogLabel.FollowPropertyError,
        },
      });
    }
  }, [followedPropertiesError, toast]);

  return (
    <StyledButtonWithLoadingState
      key={cta.text}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={handleClick}
      isLoading={loading}
      icon={cta.icon}
      iconPlacement={cta.iconPlacement}
      sizeVariant={cta.sizeVariant}
      variant={cta.variant}
    >
      {cta.text}
    </StyledButtonWithLoadingState>
  );
};
